<script setup lang="ts">
  import { computed } from 'vue';
  import { useMatrixFillLabContext } from '../../model/useMatrixFillLab';
  import FieldRef from './FieldRef.vue';
  import JsonBlock from './JsonBlock.vue';
  import LabSection from './LabSection.vue';

  const lab = useMatrixFillLabContext();

  const stateRows = computed(() => {
    const field = lab.inspected.value;
    if (!field) return [];
    return [
      { label: 'Visível', value: field.state.visible },
      { label: 'Habilitado', value: field.state.enabled },
      { label: 'Pode preencher', value: field.state.editable },
      { label: 'Obrigatório', value: field.state.required },
    ];
  });

  const valueJson = computed(() => JSON.stringify(lab.inspected.value?.value ?? null));
  const stateAndTags = computed(() =>
    lab.inspected.value ? { state: lab.inspected.value.state, tags: lab.inspected.value.tags } : null,
  );
</script>

<template>
  <LabSection
    title="Raio-X do campo"
    :badge="lab.inspected.value ? lab.elementLabels.value.get(lab.inspected.value.id) ?? lab.inspected.value.label : 'clique num campo'"
    open
    hint="Clique em qualquer campo da folha. Aqui está o nó compilado dele: estado, tags, regras e permissão que o tocam, colunas que gera no Excel."
    data-testid="lab-inspector"
  >
    <p
      v-if="!lab.inspected.value"
      class="italic text-[var(--color-gray-05)]"
    >
      Nenhum campo selecionado.
    </p>
    <template v-else>
      <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <span class="text-[var(--color-gray-05)]">Campo</span>
        <FieldRef
          :id="lab.inspected.value.id"
          :locate="false"
        />
        <span class="text-[var(--color-gray-05)]">Tipo</span>
        <code>{{ lab.inspected.value.type }}</code>
        <span class="text-[var(--color-gray-05)]">Valor atual</span>
        <code class="truncate">{{ valueJson }}</code>
      </div>

      <div class="flex flex-wrap gap-1.5">
        <span
          v-for="row in stateRows"
          :key="row.label"
          class="rounded-full px-2 py-0.5 text-[10px] font-semibold"
          :class="row.value ? 'bg-green-100 text-green-800' : 'bg-[var(--color-gray-09)] text-[var(--color-gray-05)] line-through'"
        >
          {{ row.label }}
        </span>
        <span
          v-if="lab.inspected.value.tags.anchor"
          class="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
          data-testid="inspector-tag-anchor"
        >
          Âncora
        </span>
        <span
          v-if="lab.inspected.value.tags.report"
          class="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800"
          data-testid="inspector-tag-report"
        >
          Relatório
        </span>
      </div>

      <div>
        <p class="font-semibold">
          Regras que tocam este campo
        </p>
        <p
          v-if="lab.inspectedRules.value.length === 0"
          class="italic text-[var(--color-gray-05)]"
        >
          Nenhuma.
        </p>
        <ul v-else>
          <li
            v-for="rule in lab.inspectedRules.value"
            :key="rule.id"
          >
            {{ rule.name }}: {{ rule.action }} quando "{{ rule.triggerOption }}"
            <span :class="lab.ruleFires(rule) ? 'font-semibold text-[var(--theme-secondary)]' : 'text-[var(--color-gray-05)]'">
              ({{ lab.ruleFires(rule) ? 'disparada' : 'em espera' }})
            </span>
          </li>
        </ul>
      </div>

      <div>
        <p class="font-semibold">
          Permissão
        </p>
        <p v-if="!lab.inspectedGrant.value">
          Sem permissão configurada: segue o padrão "{{ lab.defaultAccess.value === 'open' ? 'aberto' : 'fechado' }}".
        </p>
        <p v-else>
          Cargos {{ lab.inspectedGrant.value.positionIds.join(', ') || 'nenhum' }}; atividades
          {{ lab.inspectedGrant.value.functionalActivityIds.join(', ') || 'nenhuma' }}
        </p>
      </div>

      <div>
        <p class="font-semibold">
          Colunas no Excel
        </p>
        <ul>
          <li
            v-for="column in lab.inspectedColumns.value"
            :key="column.key"
          >
            {{ column.label }}
          </li>
        </ul>
      </div>

      <div v-if="lab.inspected.value.issues.length > 0">
        <p class="font-semibold text-red-700">
          Erros
        </p>
        <ul>
          <li
            v-for="issue in lab.inspected.value.issues"
            :key="issue.code"
          >
            {{ issue.message }}
          </li>
        </ul>
      </div>

      <JsonBlock
        title="Estado e tags compilados"
        :value="stateAndTags"
      />
      <JsonBlock
        title="Controle compilado (ControlSpec)"
        :value="lab.inspected.value.control"
      />
    </template>
  </LabSection>
</template>
